import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation } from 'react-router-dom';
import { Upload, Building, MapPin } from 'lucide-react';

import { ActionButton } from '@/shared/ui/action-button';
import { Badge } from '@/components/ui/badge';

import { useCurrentProjectData } from '@/entities/layout';
import { exportConsignments } from '@/store/slices/consignmentSlice';
import { HeaderSearch, FilterTrigger, HeaderSlot } from '@/features/header-filters';

import { useAppDispatch, usePermissions } from '@/shared/lib';
import { MANUAL_NODES } from '@/shared/config/manual';
import { CONSIGNMENTS_SECTION_ROUTES } from '@/shared/config/routes';
import { HelpLink, LanguageSelector } from '@/shared/ui';

export const Header: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();

  const isDashboard = location.pathname.includes('/dashboard');

  // The incoming-SRF counter belongs to the Consignments section only, and has
  // to survive moving between its two pages.
  const isConsignmentsSection = useMemo(
    () => CONSIGNMENTS_SECTION_ROUTES.some((route) => matchPath(route, location.pathname)),
    [location.pathname]
  );

  const projectData = useCurrentProjectData();
  const currentProject = projectData?.project;
  const currentSite = projectData?.site;
  const instanceInfo = projectData?.instance;
  const badges = projectData?.menu?.badges;
  const { canExportData, isReadOnly } = usePermissions();

  const handleExport = () => {
    dispatch(exportConsignments());
  };

  return (
    <header className="flex w-full justify-between items-center shrink-0 border-b border-border bg-background px-6 py-4 max-lg:flex-col max-lg:gap-4 max-lg:p-4">
      {/* Project and context section */}
      <div className="flex flex-col items-start gap-1 shrink-0 max-lg:w-full">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Building className="w-4 h-4 text-muted-foreground" />
            <h1 className="text-foreground text-lg font-semibold leading-tight">
              {currentProject?.name || instanceInfo?.name || 'RITA'}
            </h1>
          </div>
          {currentSite && (
            <div className="flex items-center gap-1 text-muted-foreground">
              <MapPin className="w-3 h-3" />
              <span className="text-sm">{currentSite.name}</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            {isConsignmentsSection && !!badges?.serviceRequests && (
              <Badge variant="destructive" className="text-xs">
                {t('cmrlist.service.requests.label', { total: badges.serviceRequests })}
              </Badge>
            )}
            {isReadOnly && (
              <Badge variant="warning" className="text-xs">
                {t('common.readOnly')}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {!isDashboard && <HeaderSearch />}

      {/* Actions */}
      <div className="flex items-center gap-2 max-lg:w-full max-lg:justify-between max-sm:flex-wrap">
        <LanguageSelector />

        <HelpLink target={MANUAL_NODES.GLOBAL_HELP} />

        <HeaderSlot name="actions" />

        <FilterTrigger />

        {!isDashboard && canExportData && (
          <ActionButton intent="document" className="h-9 gap-2" onClick={handleExport}>
            <Upload className="w-4 h-4" />
            <span className="max-sm:hidden">{t('welcome.export.label')}</span>
          </ActionButton>
        )}
      </div>
    </header>
  );
};
